import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { MatCard, MatCardContent } from '@angular/material/card';

import { FsDateModule } from '@firestitch/date';
import { FsHtmlRendererModule } from '@firestitch/html-editor';
import { FsMessage } from '@firestitch/message';
import { FsSkeletonModule } from '@firestitch/skeleton';

import { forkJoin } from 'rxjs';
import { tap } from 'rxjs/operators';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TermsTypes } from '@common/consts';
import { ContentData, SettingData } from '@common/data';
import { ITermsType } from '@common/interfaces';

import { LogoComponent } from '@libs/logo/components';


@Component({
  templateUrl: './terms-view.component.html',
  styleUrls: ['./terms-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatCard,
    MatCardContent,
    FsDateModule,
    FsHtmlRendererModule,
    FsSkeletonModule,
    LogoComponent,
  ],
})
export class TermsViewComponent {

  public termsType = signal<ITermsType | null>(null);
  public document = signal<{ content: string; effectiveDate: Date } | null>(null);

  private _route = inject(ActivatedRoute);
  private _contentData = inject(ContentData);
  private _settingData = inject(SettingData);
  private _message = inject(FsMessage);
  private _destroyRef = inject(DestroyRef);

  constructor() {
    this._init();
  }

  private _init(): void {
    // find() over the rows, never an index lookup: a plain object keyed by the raw segment
    // answers inherited keys such as "constructor".
    const termsType = TermsTypes
      .find((row: ITermsType): boolean => row.routeSegment === this._route.snapshot.params.type);

    if (!termsType) {
      this._message.error('These terms could not be found.');

      return;
    }

    this.termsType.set(termsType);

    forkJoin({
      content: this._contentData.get(termsType.contentWidgetTag),
      effectiveDate: this._settingData.get(termsType.effectiveDateSetting),
    })
      .pipe(
        tap(({ content, effectiveDate }: { content: { content: string }; effectiveDate: Date }): void => {
          this.document.set({ content: content?.content, effectiveDate });
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

}
